<template>
	<div class="balance-display">
		<div class="balance-container">
			<div v-if="cardsStore.loadingBalance" class="balance-content">
				<AppLoader :text="getString('loading')" />
			</div>
			<div v-else class="balance-content">
				<div class="balance-text">
					{{ getString("issuing_current_balance") }}
				</div>
				<div class="balance-amount">
					{{ formatBalance(currentBalance) }}
				</div>
				<p v-if="pendingBalance > 0" class="balance-pending">
					+ {{ formatBalance(pendingBalance) }} {{ getString("issuing_incoming") }}
				</p>
			</div>
		</div>
		<div class="balance-actions">
			<AppButton
				:text="getString('issuing_auto_recharge')"
				outline
				@click="handleAutoRecharge"
			/>
			<AppButton 
				:text="getString('issuing_add_funds')" 
				outline 
				@click="handleAddFunds" 
			/>
			<AppButton 
				:text="getString('issuing_payout')" 
				outline
				:disabled="!currentBalance || currentBalance <= 1"
				@click="handlePayout" 
			/>
		</div>

		<!-- Add Funds Bottom Sheet -->
		<AppBottomSheet
			:active="showAddFundsSheet"
			:force-width="bottomSheetWidthSmall"
			@toggling-popup="togglingPopup"
		>
			<ModalHeader
				:title="getString('issuing_bank_transfer_instructions')"  />
			<div class="add-funds-content">
				<!-- Test Mode Simulation Banner -->
				<div v-if="isTestMode" class="test-simulation-banner">
					<div class="test-warning">
						<span class="test-label">{{ getString('issuing_test_mode_only') }}</span>
					</div>
					<div class="test-simulate-form">
						<AppNumberInput
							double-decimal 
							:symbol="currencyStore.currCurrency.symbol"
							:label="getString('issuing_simulate_transfer_amount')"
							:value="simulateAmount"
							:placeholder="formatBalance(10000)"
							:min="1"
							:step="10"
							@changed-value="v => simulateAmount = v"
						/>
						<AppButton
							outline
							:text="getString('issuing_simulate_transfer')"
							:loading="processingSimulation"
							:disabled="!simulateAmount || simulateAmount <= 0"
							@click="handleSimulateTransfer"
						/>
					</div>
				</div>
				<div v-if="loadingFundingInstructions" class="loading-container">
					<AppLoader :text="getString('loading')" />
				</div>
				<div v-else-if="fundingInstructions" class="funding-instructions">
					<div class="instructions-header">
						<p>{{ getString('issuing_bank_transfer_description') }}</p>
					</div>
					<div class="bank-details">
						<div class="detail-row">
							<span class="detail-label">{{ getString('issuing_account_holder') }}:</span>
							<span class="detail-value">{{ fundingInstructions.account_holder_name }}</span>
						</div>
						<div class="detail-row">
							<span class="detail-label">{{ getString('issuing_iban') }}:</span>
							<span class="detail-value">{{ fundingInstructions.iban }}</span>
						</div>
						<div class="detail-row">
							<span class="detail-label">{{ getString('issuing_bic') }}:</span>
							<span class="detail-value">{{ fundingInstructions.bic }}</span>
						</div>
						<div class="detail-row">
							<span class="detail-label">{{ getString('issuing_country') }}:</span>
							<span class="detail-value">{{ fundingInstructions.country }}</span>
						</div>
					</div>
					<div class="instructions-footer">
						<p>{{ getString('issuing_bank_transfer_note') }}</p>
					</div>
				</div>
				<div v-else class="error-message">
					<p>{{ getString('issuing_funding_instructions_error') }}</p>
				</div>
			</div>
		</AppBottomSheet>

		<!-- Payout Bottom Sheet -->
		<AppBottomSheet
			:active="showPayoutSheet"
			:force-width="bottomSheetWidthSmall"
			@toggling-popup="togglingPayoutPopup"
		>
			<ModalHeader
				:title="getString('issuing_payout_instructions')"
				:button-text="getString('issuing_payout_button')"
				:button-disabled="!isPayoutValid"
				:button-loading="processingPayout"
				@clickButton="handlePayoutConfirm"
			/>
			<div class="payout-content">
				<div class="payout-description">
					<p>{{ getString('issuing_payout_description') }}</p>
				</div>
				<div class="payout-form">
					<AppNumberInput
						double-decimal :symbol="currencyStore.currCurrency.symbol"
						:label="getString('issuing_payout_amount')"
						:value="payoutAmount"
						:placeholder="formatBalance(currentBalance)"
						:max="currentBalance / 100"
						:min="1"
						:step="1"
						@changed-value="v => payoutAmount = v"
					/>
					<p class="max-available">
						{{ getString('issuing_payout_max') }}: {{ formatBalance(currentBalance) }}
					</p>
				</div>
			</div>
		</AppBottomSheet>

		<!-- Auto Recharge Bottom Sheet -->
		<AppBottomSheet
			:active="showAutoRechargeSheet"
			:force-width="bottomSheetWidthSmall"
			@toggling-popup="togglingAutoRechargePopup"
		>
			<ModalHeader
				:title="getString('issuing_auto_recharge_title')"
				:button-text="getString('save')"
				:button-disabled="!isAutoRechargeValid"
				:button-loading="processingAutoRecharge"
				@clickButton="handleSaveAutoRecharge"
			/>
			<div class="auto-recharge-content">
				<!-- Enable/Disable Switch -->
				<div class="auto-recharge-switch">
					<div class="switch-row">
						<p class="switch-label">{{ getString('issuing_auto_recharge_enable') }}</p>
						<AppSwitch 
							:checked="autoRechargeEnabled" 
							@switched="autoRechargeEnabled = $event"
						/>
					</div>
				</div>

				<!-- Mode Selection - Only shown when enabled -->
				<div v-if="autoRechargeEnabled" class="auto-recharge-modes">
					<p class="issuing-explain mb-10">{{ getString('issuing_auto_recharge_mode_explain') }}</p>
					<div class="mode-selection">
						<div 
							class="mode-select-item" 
							:class="{ active: autoRechargeMode === 'balance' }" 
							@click="setAutoRechargeMode('balance')"
						>
							<p>{{ getString('issuing_auto_recharge_mode_balance') }}</p>
						</div>
						<div 
							class="mode-select-item" 
							:class="{ active: autoRechargeMode === 'percentage' }" 
							@click="setAutoRechargeMode('percentage')"
						>
							<p>{{ getString('issuing_auto_recharge_mode_percentage') }}</p>
						</div>
					</div>

					<!-- Minimum Balance Input -->
					<div v-if="autoRechargeMode === 'balance'" class="mode-config">
						<AppNumberInput
							double-decimal 
							:symbol="currencyStore.currCurrency.symbol"
							:label="getString('issuing_auto_recharge_minimum_balance')"
							:value="minimumBalance"
							:placeholder="formatBalance(10000)"
							:min="1"
							:step="10"
							@changed-value="v => minimumBalance = v"
						/>
						<p class="mode-description">
							{{ getString('issuing_auto_recharge_minimum_balance_desc') }}
						</p>
					</div>

					<!-- Percentage Input -->
					<div v-if="autoRechargeMode === 'percentage'" class="mode-config">
						<AppNumberInput
							:label="getString('issuing_auto_recharge_percentage')"
							:value="transferPercentage"
							:placeholder="'50'"
							:min="1"
							:max="100"
							:step="1"
							right-side-symbol
							symbol="%"
							@changed-value="v => transferPercentage = v"
						/>
						<p class="mode-description">
							{{ getString('issuing_auto_recharge_percentage_desc') }}
						</p>
					</div>
				</div>
			</div>
		</AppBottomSheet>
	</div>
</template>

<script>
import { mapStores } from 'pinia';
import { useLanguageStore, useCurrencyStore } from "@/shared/stores";
import { useCardsStore } from "@/console-new/stores/cards.store";
import AppButton from "@/shared/components/AppButton.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import AppLoader from "@/shared/components/AppLoader.vue";
import AppNumberInput from "@/shared/components/AppNumberInput.vue";
import AppSwitch from "@/shared/components/AppSwitch.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import {useUserStore} from "@/console-new/stores";
import API from "@/shared/helpers/API.js";
import {useNotificationStore} from "@/shared/stores";

export default {
	setup() {
		const userStore = useUserStore();
		const cardsStore = useCardsStore();
		const languageStore = useLanguageStore();
		const currencyStore = useCurrencyStore();
		const notificationStore = useNotificationStore();
		return {
			userStore,
			languageStore,
			currencyStore,
			notificationStore,
			cardsStore
		};
	},
	name: "BalanceDisplay",
	components: {ModalHeader, AppButton, AppBottomSheet, AppLoader, AppNumberInput, AppSwitch },
	mixins: [WindowWidth],
	data() {
		return {
			showAddFundsSheet: false,
			loadingFundingInstructions: false,
			fundingInstructions: null,
			showPayoutSheet: false,
			payoutAmount: null,
			processingPayout: false,
			showAutoRechargeSheet: false,
			autoRechargeEnabled: false,
			autoRechargeMode: 'balance', // 'balance' or 'percentage'
			minimumBalance: null,
			transferPercentage: null,
			processingAutoRecharge: false,
			// Test simulation data
			simulateAmount: null,
			processingSimulation: false
		};
	},
	computed: {
		currentBalance() {
			return this.cardsStore.issuingBalance || 0;
		},
		pendingBalance() {
			return this.cardsStore.issuingPending || 0;
		},
		isTestMode() {
			// Check if running in test mode
			return TEST;
		},
		bottomSheetWidthSmall() {
			if(this.windowWidth < 550) {
				return "100%";
			} else {
				return "550px";
			}
		},
		isPayoutValid() {
			if (!this.payoutAmount || this.payoutAmount < 1) {
				return false;
			}
			// Convert to cents for comparison
			const amountInCents = Math.round(this.payoutAmount * 100);
			return amountInCents > 0 && amountInCents <= this.currentBalance;
		},
		isAutoRechargeValid() {
			if (!this.autoRechargeEnabled) {
				// Can save to disable auto recharge
				return true;
			}
			
			if (this.autoRechargeMode === 'balance') {
				// Minimum balance must be set and greater than 0
				return this.minimumBalance && this.minimumBalance > 0;
			} else if (this.autoRechargeMode === 'percentage') {
				// Percentage must be between 1 and 100
				return this.transferPercentage && this.transferPercentage >= 1 && this.transferPercentage <= 100;
			}
			
			return false;
		}
	},
	methods: {
		getString(key) {
			return this.languageStore.getString(key);
		},
		formatBalance(amountInCents) {
			return this.currencyStore.readableCurrencyValue(amountInCents, true, true, false);
		},
		handleAddFunds() {
			this.showAddFundsSheet = true;
			this.loadingFundingInstructions = true;
			this.fundingInstructions = null;

			// Call API to get funding instructions
			this.cardsStore.getFundingInstructions((data) => {
				this.loadingFundingInstructions = false;
				if (data && data.result === 'OK' && data.funding_instructions) {
					this.fundingInstructions = data.funding_instructions;
				}
			});
		},
		handleAutoRecharge() {
			this.showAutoRechargeSheet = true;
			// Load current auto recharge settings from userStore
			const business = this.userStore.currBusiness;
			if (business) {
				// Normalize the settings from business options
				const minBalance = business.issuing_balance_min;
				const percentage = business.issuing_balance_percentage;
				
				// Reset values
				this.autoRechargeEnabled = false;
				this.autoRechargeMode = 'balance';
				this.minimumBalance = null;
				this.transferPercentage = null;
				
				// Determine current state
				if (minBalance && minBalance > 0) {
					this.autoRechargeEnabled = true;
					this.autoRechargeMode = 'balance';
					this.minimumBalance = minBalance / 100; // Convert from cents to currency
				} else if (percentage && percentage > 0) {
					this.autoRechargeEnabled = true;
					this.autoRechargeMode = 'percentage';
					this.transferPercentage = percentage;
				}
			}
		},
		handlePayout() {
			this.showPayoutSheet = true;
			// Reset amount when opening
			this.payoutAmount = null;
		},
		handlePayoutConfirm() {
			if (!this.isPayoutValid || this.processingPayout) {
				return;
			}
			
			this.processingPayout = true;
			const amountInCents = Math.round(this.payoutAmount * 100);
			
			// Call API to create issuing payout
			API.init().createIssuingPayout(this.userStore.currBusinessId, amountInCents, (response) => {
				this.processingPayout = false;
				
				if (response && response.result === 'OK') {
					// Success notification
					this.notificationStore.showNotification('generic-success', this.getString('issuing_payout_success'));
					this.showPayoutSheet = false;
					this.payoutAmount = null;
					// Refresh balance after payout
					this.fetchBalance();
				} else {
					// Error notification
					this.notificationStore.showNotification('generic-fail', this.getString('issuing_payout_error'));
				}
			});
		},
		togglingPayoutPopup(value) {
			if(!value) {
				this.showPayoutSheet = false;
			}
		},
		togglingPopup(value) {
			if(!value) {
				this.showAddFundsSheet = false;
			}
		},
		togglingAutoRechargePopup(value) {
			if(!value) {
				this.showAutoRechargeSheet = false;
			}
		},
		setAutoRechargeMode(mode) {
			this.autoRechargeMode = mode;
			// Reset values when switching modes
			if (mode === 'balance') {
				this.transferPercentage = null;
			} else {
				this.minimumBalance = null;
			}
		},
		handleSaveAutoRecharge() {
			if (!this.isAutoRechargeValid || this.processingAutoRecharge) {
				return;
			}
			
			this.processingAutoRecharge = true;
			
			// Prepare data to save
			const autoRechargeData = {
				enabled: this.autoRechargeEnabled,
				mode: this.autoRechargeMode,
				minimum_balance: this.autoRechargeMode === 'balance' ? Math.round(this.minimumBalance * 100) : null,
				transfer_percentage: this.autoRechargeMode === 'percentage' ? this.transferPercentage : null
			};
			
			// Call API to save auto recharge settings
			API.init().updateAutoRechargeSettings(this.userStore.currBusinessId, autoRechargeData, (response) => {
				this.processingAutoRecharge = false;
				
				if (response && response.result === 'OK') {
					// Update the userStore with new values
					if (this.userStore.currBusiness) {
						if (!this.autoRechargeEnabled) {
							this.userStore.currBusiness.issuing_balance_min = null;
							this.userStore.currBusiness.issuing_balance_percentage = null;
						} else if (this.autoRechargeMode === 'balance') {
							this.userStore.currBusiness.issuing_balance_min = Math.round(this.minimumBalance * 100);
							this.userStore.currBusiness.issuing_balance_percentage = null;
						} else {
							this.userStore.currBusiness.issuing_balance_min = null;
							this.userStore.currBusiness.issuing_balance_percentage = this.transferPercentage;
						}
					}
					
					this.notificationStore.showNotification('generic-success', this.getString('issuing_auto_recharge_success'));
					this.showAutoRechargeSheet = false;
				} else {
					this.notificationStore.showNotification('generic-fail', this.getString('issuing_auto_recharge_error'));
				}
			});
		},
		fetchBalance() {
			this.cardsStore.getIssuingBalance();
		},
		handleSimulateTransfer() {
			if (!this.simulateAmount || this.simulateAmount <= 0 || this.processingSimulation) {
				return;
			}
			
			this.processingSimulation = true;
			const amountInCents = Math.round(this.simulateAmount * 100);
			
			// Call API to simulate test transfer
			API.init().simulateTestTransfer(this.userStore.currBusinessId, amountInCents, (response) => {
				this.processingSimulation = false;
				
				if (response && response.result === 'OK') {
					// Success notification
					this.notificationStore.showNotification('generic-success', this.getString('issuing_simulate_transfer_success'));
					this.simulateAmount = null;
					this.showAddFundsSheet = false;
					// Refresh balance after simulation
					this.fetchBalance();
				} else {
					// Error notification
					this.notificationStore.showNotification('generic-fail', this.getString('issuing_simulate_transfer_error'));
				}
			});
		}
	},
	mounted() {
		this.fetchBalance();
	},
	watch: {
		'userStore.currBusinessId': function (newVal, oldVal) {
			if (newVal !== oldVal) {
				this.fetchBalance();
			}
		}
	}
};
</script>

<style scoped lang="scss">
.balance-display {
	width: 100%;

	.balance-container {
		background-color: var(--color-bg-primary-2);
		border-radius: 13px;
		box-shadow: -2px -2px 10px 0px var(--color-box-shadow-2) inset, 2px 2px 20px 0px var(--color-box-shadow-2) inset;
		min-height: 270px;
		display: flex;
		justify-content: center;
		align-items: center;
		position: relative;

		.balance-content {
			text-align: center;
			padding: 20px;

			.balance-text {
				font-size: 18px;
				color: var(--color-text-secondary);
				margin-bottom: 10px;
				font-weight: 500;
			}

			.balance-amount {
				font-size: 48px;
				font-weight: 700;
				color: var(--color-text-primary);
				line-height: 1.2;
			}
			
			.balance-pending {
				font-size: 14px;
				color: var(--color-success);
				margin-top: 8px;
				margin-bottom: 0;
				font-weight: 500;
			}
		}
	}

	.balance-actions {
		display: flex;
		justify-content: center;
		align-items: center;
		gap: 10px;
		margin-top: 20px;
		height: 40px;

		.base-button {
			height: 100%;
			flex: 1;
		}
	}

	@media (max-width: 768px) {
		.balance-container {
			min-height: 200px;

			.balance-content {
				.balance-amount {
					font-size: 36px;
				}
			}
		}
	}
}

.add-funds-content {

	.loading-container {
		display: flex;
		justify-content: center;
		align-items: center;
		min-height: 200px;
	}

	.funding-instructions {
		.instructions-header {
			margin-bottom: 10px;

			h3 {
				font-size: 18px;
				font-weight: 600;
				color: var(--color-text-primary);
				margin-bottom: 10px;
			}

			p {
				font-size: 14px;
				color: var(--color-text-secondary);
			}
		}

		.bank-details {
			background-color: var(--color-background-secondary);
			border-radius: 8px;
			padding: 20px;
			margin-bottom: 20px;

			@media (max-width: 500px) {
				padding: 0px;
			}

			.detail-row {
				display: flex;
				justify-content: space-between;
				align-items: center;
				flex-wrap: wrap;
				padding: 10px 0;
				border-bottom: 1px solid var(--color-border);
				gap: 10px;

				&:last-child {
					border-bottom: none;
				}

				.detail-label {
					font-size: 14px;
					font-weight: 500;
					color: var(--color-text-secondary);
				}

				.detail-value {
					font-size: 14px;
					font-weight: 600;
					color: var(--color-text-primary);
					font-family: monospace;
					text-align: right;
					margin-left: auto;
				}
			}
		}

		.instructions-footer {
			p {
				font-size: 13px;
				color: var(--color-text-secondary);
				font-style: italic;
			}
		}
	}

	.error-message {
		text-align: center;
		padding: 40px 20px;

		p {
			font-size: 14px;
			color: var(--color-error);
		}
	}
}

.payout-content {
	.payout-description {
		margin-bottom: 20px;

		p {
			font-size: 14px;
			color: var(--color-text-secondary);
		}
	}

	.payout-form {
		display: flex;
		flex-direction: column;
		align-items: flex-end;
		justify-content: center;

		.max-available {
			font-size: 13px;
			color: var(--color-text-secondary);
			margin-top: 10px;
			text-align: right;
		}
	}
}

.auto-recharge-content {
	.auto-recharge-switch {

		.switch-row {
			display: flex;
			justify-content: space-between;
			align-items: center;
			padding: 10px 0;
			
			.switch-label {
				font-size: 16px;
				font-weight: 500;
				color: var(--color-text-primary);
			}
		}
	}
	
	.auto-recharge-modes {
		.issuing-explain {
			font-size: 14px;
			color: var(--color-txt-1);
			margin-bottom: 10px;
		}
		.mode-selection {
			display: flex;
			border-radius: 13px;
			background-color: var(--color-bg-primary-2);
			box-shadow: inset 2px 2px 3px var(--color-box-shadow-3);
			padding: 8px;
			gap: 6px;
			margin-bottom: 20px;
			
			.mode-select-item {
				border-radius: 13px;
				border: 1px solid var(--color-primary);
				padding: 7px 12px;
				cursor: pointer;
				user-select: none;
				transition: background-color 0.3s ease;
				display: flex;
				align-items: center;
				justify-content: center;
				height: 40px;
				width: 50%;
				
				p {
					font-size: 14px;
					color: var(--color-primary);
					text-align: center;
					word-break: break-word;
					line-height: 1.2;
				}
				
				&.active {
					background-color: var(--color-active-button);
					
					p {
						color: var(--color-active-button-text);
					}
				}
			}
		}
		
		.mode-config {
			display: flex;
			align-items: flex-end;
			flex-direction: column;
			.mode-description {
				font-size: 13px;
				color: var(--color-text-secondary);
				margin-top: 10px;
				line-height: 1.4;
			}
		}
	}
}

.test-simulation-banner {
	background-color: var(--color-bg-primary-2);
	border: 2px dashed #FFC107;
	border-radius: 8px;
	padding: 16px;
	margin-bottom: 20px;

	.test-warning {
		margin-bottom: 16px;
		text-align: center;

		.test-label {
			background-color: #FFC107;
			color: #000;
			padding: 4px 12px;
			border-radius: 16px;
			font-size: 12px;
			font-weight: 600;
			text-transform: uppercase;
			letter-spacing: 0.5px;
		}
	}

	.test-simulate-form {
		display: flex;
		flex-direction: row;
		flex-wrap: wrap;
		justify-content: space-between;
		gap: 12px;
		align-items: flex-end;

		.base-button {
			min-width: 140px;
			flex-shrink: 0;
		}
	}
}
</style>